<script setup lang="ts">
const props = withDefaults(defineProps<{
  src?: string | null
  alt?: string
  width?: string | number
  height?: string | number
  cover?: boolean
  contain?: boolean
  aspectRatio?: string | number
  lazy?: boolean
  /** LCP-Bild: lädt eager + fetchpriority=high. Überschreibt `lazy`. */
  priority?: boolean
  /** Ziel-Breite (px) für serverseitiges Downscaling externer Bilder via Proxy. */
  proxyWidth?: number
  rounded?: string
  maxWidth?: string | number
  maxHeight?: string | number
}>(), {
  alt: '',
  lazy: true,
  priority: false,
})

// LCP-Bilder (priority) nie lazy; sonst Default-lazy.
const loadingAttr = computed(() => (props.priority ? 'eager' : props.lazy ? 'lazy' : 'eager'))
const fetchPriorityAttr = computed(() => (props.priority ? 'high' : undefined))

const loaded = ref(false)
const error = ref(false)
const imgRef = ref<HTMLImageElement | null>(null)

function isExternal(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://')
}

const resolvedSrc = computed(() => {
  if (!props.src) return null
  if (isExternal(props.src)) {
    const w = props.proxyWidth ? `&w=${props.proxyWidth}` : ''
    return `/api/image-proxy?url=${encodeURIComponent(props.src)}${w}`
  }
  return props.src
})

function onLoad() { loaded.value = true }
function onError() { error.value = true }

// SSR-Hydration: Native Image-Loading kann @load feuern BEVOR Vue die
// Listener bindet. `img.complete && naturalWidth > 0` fängt diesen Fall ab —
// ohne das blieb das Bild mit opacity:0 hängen.
onMounted(() => {
  const el = imgRef.value
  if (el?.complete && el.naturalWidth > 0) {
    loaded.value = true
  }
})
</script>

<template>
  <div
    class="m-img"
    :style="{
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
      maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
      aspectRatio: aspectRatio ? String(aspectRatio) : undefined,
    }"
  >
    <img
      v-if="resolvedSrc && !error"
      ref="imgRef"
      :src="resolvedSrc"
      :alt="alt"
      :loading="loadingAttr"
      :fetchpriority="fetchPriorityAttr"
      decoding="async"
      class="m-img__img"
      :class="{
        'm-img--cover': cover,
        'm-img--contain': contain,
        'm-img--loaded': loaded,
      }"
      @load="onLoad"
      @error="onError"
    />
    <div v-if="!loaded && !error" class="m-img__placeholder">
      <slot name="placeholder">
        <div class="m-img__skeleton" />
      </slot>
    </div>
    <div v-if="error" class="m-img__error">
      <slot name="error">
        <svg viewBox="0 0 48 48" :width="32" :height="32" fill="none" stroke="#ccc" stroke-width="1.5">
          <rect x="6" y="10" :width="36" :height="28" rx="3" />
          <circle cx="16" cy="20" r="3" />
          <path d="M6 32l10-8 6 5 8-6 12 9" />
        </svg>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.m-img {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.m-img__img {
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity var(--m-transition);
}

.m-img__img.m-img--loaded { opacity: 1; }
.m-img__img.m-img--cover { object-fit: cover; }
.m-img__img.m-img--contain { object-fit: contain; }

.m-img__placeholder,
.m-img__error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.m-img__skeleton {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: m-skeleton 1.5s ease-in-out infinite;
}

@keyframes m-skeleton {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
